import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  useNoticeBannersStackContext,
  useResponsive,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { getUrqlError } from '@order/utils';

import { useActiveRouteName } from '../../navigation';
import { usePromoCodeTelemetry } from './usePromoCodeTelemetry';
import { useSubmitPromoCode } from './useSubmitPromoOrGiftCardCode';

export const usePromoCode = (refetchCredits?: () => void) => {
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const [promoCode, setPromoCode] = useState('');
  const [promoCodePreviouslyEntered, setPromoCodePreviouslyEntered] =
    useState(false);
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false);
  const [isPromoCodeAlreadyApplied, setIsPromoCodeAlreadyApplied] =
    useState(false);
  const routeName = useActiveRouteName();

  const { submitCodeResponse, submitCode } = useSubmitPromoCode();
  const { fetching: isSubmittingPromoCode } = submitCodeResponse;

  const {
    monitorPromoCodeApplied,
    monitorPromoCodeFailed,
    monitorPromoCodeEntered,
    monitorPromoCodeAttemptApply,
  } = usePromoCodeTelemetry();

  const handleSetPromoCode = useCallback(
    (input: string) => {
      setPromoCode(input);
      setPromoCodeInvalid(false);

      // Trigger promo code entered event once per session
      if (input.length > 0 && !promoCodePreviouslyEntered) {
        setPromoCodePreviouslyEntered(true);
        monitorPromoCodeEntered(routeName);
      }
    },
    [monitorPromoCodeEntered, promoCodePreviouslyEntered, routeName],
  );

  const submitPromoCode = useCallback(async () => {
    monitorPromoCodeAttemptApply(routeName);
    const { isSubmitSuccess, typename, error, isAlreadyApplied } =
      await submitCode(promoCode);

    setPromoCodeInvalid(!isSubmitSuccess);
    setIsPromoCodeAlreadyApplied(isAlreadyApplied);

    if (!isSubmitSuccess) {
      monitorPromoCodeFailed(
        formatMessage(messages.accountNoticeCreditInvalid),
        getUrqlError(error),
        routeName,
      );

      return;
    }

    setPromoCode('');
    setPromoCodePreviouslyEntered(false);
    monitorPromoCodeApplied(`${typename}`, routeName);

    if (currentBreakpoint.isXS && isExpoRouterEnabled) {
      router?.back();
    }

    if (
      currentBreakpoint.isXS &&
      !isExpoRouterEnabled &&
      navigation?.canGoBack()
    ) {
      navigation.goBack();
    }

    addNoticeBanner({
      text: formatMessage(
        typename === 'SubmitPromoCodeSuccessV2'
          ? messages.accountNoticePromoApplied
          : messages.accountNoticeCreditApplied,
      ),
      palette: 'success',
    });

    refetchCredits?.();
  }, [
    promoCode,
    currentBreakpoint.isXS,
    isExpoRouterEnabled,
    router,
    navigation,
    routeName,
    formatMessage,
    addNoticeBanner,
    monitorPromoCodeFailed,
    monitorPromoCodeApplied,
    monitorPromoCodeAttemptApply,
    refetchCredits,
    submitCode,
  ]);

  return {
    promoCode,
    isSubmittingPromoCode,
    isPromoCodeApplied: isPromoCodeAlreadyApplied,
    promoCodeInvalid,
    setPromoCode: handleSetPromoCode,
    submitPromoCode,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountNoticePromoApplied: {
    defaultMessage:
      'Your promo has been added as a reward. Apply it to an active order in your bag.',
    description: 'Promo Code | Notice | Promo Applied',
  },
  accountNoticeCreditApplied: {
    defaultMessage: 'Credit added successfully',
    description: 'Promo Code | Notice | Credit Applied',
  },
  accountNoticeCreditInvalid: {
    defaultMessage: 'This code or giftcard is not valid.',
    description: 'Account | Notice | Credit Invalid',
  },
});
